/**
 * The brand's binding of the shared {@link TimelineSection}.
 *
 * The mirror of `ManufacturerTimelineSection`, and deliberately as thin: a
 * brand's history and a manufacturer's differ in what they are a history OF,
 * not in how they are stored or edited, so everything else is shared.
 *
 * No `hideTimelineFromPrimaryBrand` here — that flag governs the
 * manufacturer→primary-brand link and has no brand-side equivalent, which is
 * why the shared section takes it as an optional `headerExtra` rather than
 * knowing about it.
 */

import type { TimelineEvent, TimelineEventImage } from '@watchkeeper/shared-types';
// The ADMIN's WatchBrand — `Omit<Shared, …>` with Firestore `Timestamp`s, so it
// inherits `history` while staying assignable from what the editor holds.
import type { WatchBrand } from '../../types';
import { TimelineSection, type TimelineOwner } from '../Timeline/TimelineSection';
import {
  enrichBrandHistoryWithAI,
  updateWatchBrand,
  uploadBrandHistoryHeroImage,
  uploadBrandTimelineImage,
} from '../../services/firebase';

interface Props {
  brand: WatchBrand;
  /** Lets the host refresh its copy after a write. */
  onBrandChange?: (next: WatchBrand) => void;
}

export function BrandTimelineSection({ brand, onBrandChange }: Props) {
  const owner: TimelineOwner = {
    kind: 'brand',
    id: brand.id,
    name: brand.displayName || brand.name || brand.id,
    history: brand.history,
    storagePrefix: `brands/${brand.id}/timeline/`,
    persist: async (history: TimelineEvent[]) => {
      await updateWatchBrand(brand.id, { history });
      onBrandChange?.({ ...brand, history });
    },
    uploadImage: (file, fileName) => uploadBrandTimelineImage(brand.id, file, fileName),
    enrich: (input) => enrichBrandHistoryWithAI({ brandId: brand.id, ...input }),
    historyHeroImage: brand.historyHeroImage,
    // NOTE: needs `match /brands/{brandId}/history-hero/{fileName}` in
    // watchlock's storage.rules. The catch-all `brands/{allPaths=**}` grants
    // READ only — see the correction on `uploadBrandTimelineImage`.
    heroStoragePrefix: `brands/${brand.id}/history-hero/`,
    uploadHeroImage: (file, fileName) => uploadBrandHistoryHeroImage(brand.id, file, fileName),
    persistHeroImage: async (historyHeroImage: TimelineEventImage | null) => {
      // `null`, never `undefined`. Both updaters run a shallow `stripUndefined`
      // before `updateDoc`, and `updateDoc` merges — so an undefined value here
      // would be dropped from the write and the OLD hero would survive a
      // Remove, looking saved until the page was reopened. `null` survives the
      // strip and erases the field. The updater's parameter type does not admit
      // null, hence the widening cast.
      await updateWatchBrand(brand.id, {
        historyHeroImage,
      } as unknown as Partial<Omit<WatchBrand, 'id'>>);
      onBrandChange?.({ ...brand, historyHeroImage: historyHeroImage ?? undefined });
    },
  };

  return <TimelineSection owner={owner} />;
}
